import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Save } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { latePolicyService } from '@/services/latePolicyService'
import { showSaveSuccess } from '@/lib/swal'

export type LatePolicyForm = {
  id?: number
  graceTimeInMins: number
  deductionMethod: number
  lateCountPerDeduction: number
  isAnnualLeaveDeductable: boolean
  branchId: number
}

const emptyForm = (branchId: number): LatePolicyForm => ({
  id: undefined,
  graceTimeInMins: 0,
  deductionMethod: 0,
  lateCountPerDeduction: 0,
  isAnnualLeaveDeductable: true,
  branchId,
})

export const LatePolicy: React.FC = () => {
  const { t } = useTranslation('common')
  const branchId = useAuthStore((s) => s.user?.selectedBranch?.branchId ?? 0)
  const branchName = useAuthStore((s) => s.user?.selectedBranch?.branchName ?? '')

  const [saving, setSaving] = useState(false)
  const [loadingPolicy, setLoadingPolicy] = useState(false)
  const [loadingMethods, setLoadingMethods] = useState(false)
  const [deductionMethods, setDeductionMethods] = useState<Array<{ id: number; name: string }>>([])
  const [form, setForm] = useState<LatePolicyForm>(emptyForm(branchId))

  // If branch changes at runtime, keep form in sync
  const normalizedForm = useMemo(() => {
    return { ...form, branchId }
  }, [form, branchId])

  const validate = (value: LatePolicyForm): string | null => {
    if (!value.branchId) return 'Please select a branch first'
    if (value.graceTimeInMins < 0) return 'Grace time must be 0 or greater'
    if (value.deductionMethod < 0) return 'Deduction method must be 0 or greater'
    if (value.lateCountPerDeduction < 0) return 'Late count per deduction must be 0 or greater'
    return null
  }

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        setLoadingMethods(true)
        const list = await latePolicyService.getDeductionMethods()
        if (cancelled) return
        setDeductionMethods(list)
        // If current value isn't present, default to first option (if any)
        if (list.length > 0 && !list.some((x) => x.id === normalizedForm.deductionMethod)) {
          setForm((p) => ({ ...p, deductionMethod: list[0].id }))
        }
      } catch (e: any) {
        if (cancelled) return
        setDeductionMethods([])
        toast.error(e?.response?.data?.message || e?.message || 'Failed to load deduction methods')
      } finally {
        if (!cancelled) setLoadingMethods(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!branchId) return
    let cancelled = false

    void (async () => {
      try {
        setLoadingPolicy(true)
        const dto = await latePolicyService.getByBranchId(branchId)
        if (cancelled) return

        const next = dto
          ? {
              id: dto.id,
              graceTimeInMins: Number(dto.graceTimeInMins || 0),
              deductionMethod: Number(dto.deductionMethod || 0),
              lateCountPerDeduction: Number(dto.lateCountPerDeduction || 0),
              isAnnualLeaveDeductable: Boolean(dto.isAnnualLeaveDeductable),
              branchId,
            }
          : emptyForm(branchId)

        setForm(next)
      } catch (e: any) {
        if (cancelled) return
        setForm(emptyForm(branchId))
        toast.error(e?.response?.data?.message || e?.message || 'Failed to load late policy')
      } finally {
        if (!cancelled) setLoadingPolicy(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [branchId])

  const handleSave = async () => {
    const err = validate(normalizedForm)
    if (err) {
      toast.error(err)
      return
    }

    try {
      setSaving(true)
      const res: any = await latePolicyService.saveOrUpdate({
        id: normalizedForm.id,
        graceTimeInMins: normalizedForm.graceTimeInMins,
        deductionMethod: normalizedForm.deductionMethod,
        lateCountPerDeduction: normalizedForm.lateCountPerDeduction,
        isAnnualLeaveDeductable: normalizedForm.isAnnualLeaveDeductable,
        branchId: normalizedForm.branchId,
      })

      const msgRaw =
        res?.result?.msg ??
        res?.result?.Msg ??
        res?.Result?.msg ??
        res?.Result?.Msg ??
        res?.message ??
        res?.Message

      const msg = typeof msgRaw === 'string' ? msgRaw.trim() : ''
      const isEditMode = /\b(update|updated|edit|edited)\b/i.test(msg)
      await showSaveSuccess(isEditMode, msg || 'Late policy saved')
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to save late policy')
    } finally {
      setSaving(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={t('latePolicy.title')} description={t('latePolicy.description')} />
        <Card>
          <CardContent className="p-6">
            <p className="text-muted-foreground">Please select a branch first.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader title={t('latePolicy.title')} description={t('latePolicy.description')} />

      <Card>
        <CardHeader className="bg-gradient-to-r from-primary/10 to-secondary/10 border-b">
          <CardTitle className="text-lg font-semibold">{t('latePolicy.title')}</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>{t('latePolicy.deductionMethod')}</Label>
              <Select
                value={String(normalizedForm.deductionMethod)}
                onValueChange={(value) => setForm((p) => ({ ...p, deductionMethod: Number(value) }))}
                disabled={saving || loadingPolicy || loadingMethods || deductionMethods.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder={loadingMethods ? 'Loading...' : 'Select'} />
                </SelectTrigger>
                <SelectContent searchable>
                  {deductionMethods.map((m) => (
                    <SelectItem key={m.id} value={String(m.id)}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('latePolicy.graceTimeInMins')}</Label>
              <Input
                type="number"
                min={0}
                value={normalizedForm.graceTimeInMins}
                onChange={(e) =>
                  setForm((p) => ({ ...p, graceTimeInMins: Number(e.target.value || 0) }))
                }
                disabled={saving || loadingPolicy}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('latePolicy.lateCountPerDeduction')}</Label>
              <Input
                type="number"
                min={0}
                value={normalizedForm.lateCountPerDeduction}
                onChange={(e) =>
                  setForm((p) => ({ ...p, lateCountPerDeduction: Number(e.target.value || 0) }))
                }
                disabled={saving || loadingPolicy}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('latePolicy.branch')}</Label>
              <Input value={branchName || String(branchId)} readOnly className="bg-muted" />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label className="block">{t('latePolicy.isAnnualLeaveDeductable')}</Label>
              <div className="flex items-center gap-2 rounded-md border p-3">
                <Checkbox
                  id="isAnnualLeaveDeductable"
                  checked={normalizedForm.isAnnualLeaveDeductable}
                  onCheckedChange={(checked) =>
                    setForm((p) => ({ ...p, isAnnualLeaveDeductable: Boolean(checked) }))
                  }
                  disabled={saving || loadingPolicy}
                />
                <Label htmlFor="isAnnualLeaveDeductable" className="cursor-pointer">
                  {t('latePolicy.isAnnualLeaveDeductable')}
                </Label>
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-end gap-2">
            <Button type="button" onClick={handleSave} disabled={saving || loadingPolicy}>
              <Save className="mr-2 h-4 w-4" />
              {t('latePolicy.save')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

